import { useCallback, useEffect, useRef, useState } from "react";
import Button from "../ui/Button";
import { useCashierText } from "./cashierText";

const CAMERA_STATES = {
  INITIALIZING: "initializing",
  LIVE: "live",
  CAPTURED: "captured",
  UPLOADING: "uploading",
  ERROR: "error",
};

function CameraSchematic({ type, text }) {
  if (type === "invoice") return <div className="camera-receipt">{text.appInvoice}</div>;
  if (type === "order")
    return (
      <div className="camera-products">
        <div className="camera-drink" />
        <div className="camera-burger" />
        <div className="camera-plate" />
      </div>
    );
  if (type === "both")
    return (
      <>
        <div className="camera-bag">{text.packedOrder}</div>
        <div className="camera-receipt">{text.invoice}</div>
      </>
    );
  return (
    <>
      <div className="camera-phone">{text.phone}</div>
      <div className="camera-bag">{text.order}</div>
      <div className="camera-receipt">{text.invoice}</div>
    </>
  );
}

export default function CameraCapture({
  open,
  evidence,
  progress,
  onClose,
  onConfirm,
}) {
  const video = useRef(null);
  const input = useRef(null);
  const stream = useRef(null);
  const [cameraState, setCameraState] = useState(CAMERA_STATES.INITIALIZING);
  const [capture, setCapture] = useState(null);
  const [error, setError] = useState("");
  const [facingMode, setFacingMode] = useState("environment");
  const [cameraSession, setCameraSession] = useState(0);
  const { t } = useCashierText();
  const text = { ...t, ...t.camera };
  const startFailed = text.startFailed;

  const stopCamera = useCallback(() => {
    stream.current?.getTracks().forEach((track) => track.stop());
    stream.current = null;
    if (video.current) video.current.srcObject = null;
  }, []);

  const clearCapture = useCallback(() => {
    setCapture((current) => {
      if (current?.previewUrl) URL.revokeObjectURL(current.previewUrl);
      return null;
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    setCameraState(CAMERA_STATES.INITIALIZING);
    setError("");
    clearCapture();
    let alive = true;
    const getUserMedia = navigator.mediaDevices?.getUserMedia;
    if (!getUserMedia) {
      setCameraState(CAMERA_STATES.ERROR);
      setError(startFailed);
      return () => clearCapture();
    }
    getUserMedia
      .call(navigator.mediaDevices, {
        video: { facingMode: { ideal: facingMode } },
        audio: false,
      })
      .then((value) => {
        if (!alive) return value.getTracks().forEach((track) => track.stop());
        stream.current = value;
        if (video.current) video.current.srcObject = value;
        setCameraState(CAMERA_STATES.LIVE);
      })
      .catch(() => {
        setCameraState(CAMERA_STATES.ERROR);
        setError(startFailed);
      });
    return () => {
      alive = false;
      stopCamera();
      clearCapture();
    };
  }, [open, evidence?.key, facingMode, cameraSession, stopCamera, clearCapture, startFailed]);

  if (!open) return null;

  const prepareCapture = (file) => {
    if (!(file instanceof Blob)) {
      setError(text.prepareFailed);
      return;
    }
    stopCamera();
    clearCapture();
    setCapture({ file, previewUrl: URL.createObjectURL(file) });
    setError("");
    setCameraState(CAMERA_STATES.CAPTURED);
  };

  const capturePhoto = () => {
    if (!video.current?.videoWidth) return input.current?.click();
    const canvas = document.createElement("canvas");
    canvas.width = video.current.videoWidth;
    canvas.height = video.current.videoHeight;
    canvas.getContext("2d").drawImage(video.current, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (blob)
          prepareCapture(
          new File([blob], `${evidence.key}-${Date.now()}.jpg`, {
            type: "image/jpeg",
          }),
          );
      },
      "image/jpeg",
      0.9,
    );
  };

  const confirmPhoto = async () => {
    if (!capture?.file || cameraState === CAMERA_STATES.UPLOADING) return;
    setCameraState(CAMERA_STATES.UPLOADING);
    setError("");
    let succeeded = false;
    try {
      await onConfirm(capture.file);
      succeeded = true;
      clearCapture();
    } catch (uploadError) {
      setError(uploadError.message || text.uploadFailed);
    } finally {
      // On success the parent advances/closes the camera. On failure the same
      // File and preview remain available for retry.
      if (!succeeded) setCameraState(CAMERA_STATES.CAPTURED);
    }
  };

  const retakePhoto = () => {
    clearCapture();
    setError("");
    setCameraState(CAMERA_STATES.INITIALIZING);
    setCameraSession((value) => value + 1);
  };

  const restartCamera = () => {
    clearCapture();
    setError("");
    setCameraState(CAMERA_STATES.INITIALIZING);
    setFacingMode((value) => (value === "environment" ? "user" : "environment"));
  };

  const isReview =
    cameraState === CAMERA_STATES.CAPTURED ||
    cameraState === CAMERA_STATES.UPLOADING;

  return (
    <div className={`cashier-camera ${isReview ? "is-reviewing" : ""}`}>
      <div className="cashier-camera-top">
        <div>
          <b>{evidence.label}</b>
          <small>{isReview ? text.confirmPhoto : text.guide}</small>
        </div>
        <Button
          variant="primary"
          appearance="filled"
          size="sm"
          onClick={onClose}
          disabled={cameraState === CAMERA_STATES.UPLOADING}
        >
          {text.close}
        </Button>
      </div>
      {isReview ? (
        <div className="cashier-camera-confirmation">
          <h3>{text.confirmPhoto}</h3>
          <div className="cashier-camera-confirm-card">
            <img src={capture?.previewUrl} alt={text.preview(evidence.label)} />
            <div className="cashier-camera-confirm-meta">
              {evidence.label}
              {progress && <><br />{text.progress(progress.current, progress.total)}</>}
            </div>
          </div>
          <div className="cashier-camera-question">{text.photoClearQuestion}</div>
          {error && <div className="cashier-camera-error" role="alert">{error}</div>}
          <div className="cashier-camera-confirm-actions">
            <Button
              variant="neutral"
              onClick={retakePhoto}
              disabled={cameraState === CAMERA_STATES.UPLOADING}
            >
              {text.retake}
            </Button>
            <Button
              appearance="filled"
              onClick={confirmPhoto}
              disabled={cameraState === CAMERA_STATES.UPLOADING}
            >
              {cameraState === CAMERA_STATES.UPLOADING ? text.uploading : error ? text.retry : text.yesSave}
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="cashier-camera-view">
            {cameraState !== CAMERA_STATES.ERROR && <video ref={video} autoPlay playsInline muted />}
            <div className={`cashier-guide guide-${evidence.guideType || evidence.key}`} data-guide={evidence.guideType || evidence.key}>
              <i /><i /><i /><i />
              <div className="cashier-camera-schematic"><CameraSchematic type={evidence.guideType || evidence.key} text={text} /></div>
              <div className="cashier-guide-text">
                <b>{evidence.label}</b>
                <p>{evidence.guide}</p>
                {progress && <span>{progress.current} / {progress.total}</span>}
              </div>
            </div>
            {cameraState === CAMERA_STATES.INITIALIZING && <div className="camera-loading">{text.starting}</div>}
            {cameraState === CAMERA_STATES.ERROR && <div className="camera-fallback">{error}</div>}
          </div>
          <div className="cashier-camera-bottom">
            <Button variant="primary" appearance="filled" onClick={() => input.current?.click()}>
              {text.gallery}
            </Button>
            <button className="cashier-shutter" onClick={capturePhoto} aria-label={text.takePhoto} disabled={cameraState === CAMERA_STATES.INITIALIZING} />
            <Button variant="primary" appearance="filled" onClick={restartCamera}>
              {text.switch}
            </Button>
          </div>
        </>
      )}
      <input
        ref={input}
        hidden
        type="file"
        accept="image/*"
        capture="environment"
        onChange={(e) => {
          const file = e.target.files[0];
          if (file) prepareCapture(file);
          e.target.value = "";
        }}
      />
    </div>
  );
}
